/**
 * workspaceStore.js
 *
 * Framework-free store behind the workspace dashboard. It owns the state, the
 * offline cache and the debounced account save; React only subscribes
 * (`useWorkspaces.js`). All IO is injected, so the whole lifecycle - cold start
 * offline, migration, optimistic edit, debounced per-namespace save, cross-device
 * adopt - is assertable from Jest with fakes.
 *
 * Read path   local mirror first (instant paint, works offline) -> account copy
 *             (cross-device truth; `updatedAt` decides, a pending local edit is
 *             never clobbered by a slower read of the pre-edit state).
 * Write path  optimistic: state updates now, mirror is written now, the account
 *             write is debounced and fire-and-forget; a failed save never rolls
 *             the UI back (the mirror holds the truth, the next save re-sends it).
 *
 * "Enabled" means a `mobileWorkspaces` entry exists. Until then the Manager tab
 * shows the unchanged classic dashboard; `enable()` runs the migration (one
 * `classic` workspace standing for the existing layout) and from then on the tab
 * opens the workspace dashboard. `disable()` deletes ONLY this namespace -
 * admin layouts and the legacy `mobileManagement` layout are never touched.
 *
 * @module helpers/workspaces/workspaceStore
 */

import {
    WORKSPACES_NAMESPACE,
    buildSavePatch,
    fitsBudget,
    normalizeState,
    pickNewer,
    resolveInitialState,
    setActiveBoard,
} from './workspaceModel';

/** Local mirror key (persistentSettings). */
export const WORKSPACES_CACHE_KEY = 'MOBILE_WORKSPACES_STATE';

/** Debounce before the account write; batches a burst of edits. */
export const PERSIST_DEBOUNCE_MS = 700;

/**
 * Create a store.
 *
 * @param {object} io Injected IO.
 * @param {() => Promise<*>} io.readMirror Read the local mirror (`{state, synced}` or `null`).
 * @param {(value: ?object) => Promise<void>} io.writeMirror Write (or clear with `null`) the local mirror.
 * @param {(namespace: string) => Promise<object>} io.readRemote Read one namespace from the account (`{}` when absent).
 * @param {(patch: object) => Promise<*>} io.saveRemote Write namespaces to the account (per-namespace merge server side).
 * @param {() => Date} [io.now] Clock.
 * @param {(handler: Function, ms: number) => *} [io.setTimer] Timer (tests).
 * @param {(handle: *) => void} [io.clearTimer] Timer clear (tests).
 * @returns {object} The store.
 */
export const createWorkspaceStore = ({
    readMirror,
    writeMirror,
    readRemote,
    saveRemote,
    now = () => new Date(),
    setTimer = (handler, ms) => globalThis.setTimeout(handler, ms),
    clearTimer = (handle) => globalThis.clearTimeout(handle),
}) => {
    const listeners = new Set();
    let snapshot = {ready: false, loading: false, enabled: false, saving: false, offline: false, error: null, state: null};
    let synced = false;
    let timer = null;
    let loadPromise = null;
    let migrationName = null;

    const emit = (patch) => {
        snapshot = {...snapshot, ...patch};
        listeners.forEach((listener) => listener());
    };

    const flushSave = async () => {
        timer = null;

        if (!snapshot.state || !snapshot.enabled) {
            return;
        }

        const sent = snapshot.state;

        emit({saving: true});

        try {
            await saveRemote(buildSavePatch(sent, now()));
            synced = true;
            // The edit may have changed again while the request was in flight; only
            // mark the mirror clean when what we sent is still current.
            if (snapshot.state === sent) {
                await writeMirror({state: sent, synced: true});
            }
            emit({saving: !!timer, offline: false});
        } catch (_error) {
            // Non-fatal: the mirror already holds the truth and the next edit re-sends it.
            emit({saving: false, offline: true});
        }
    };

    const schedule = () => {
        if (timer) {
            clearTimer(timer);
        }

        timer = setTimer(flushSave, PERSIST_DEBOUNCE_MS);
    };

    const adopt = (state, enabled) => {
        emit({state, enabled, ready: true});
    };

    const load = async () => {
        emit({loading: true});

        let mirror = null;

        try {
            mirror = await readMirror();
        } catch (_error) {
            mirror = null;
        }

        const local = mirror && normalizeState(mirror.state);

        synced = !!(mirror && mirror.synced);

        if (local) {
            adopt(local, true);
        } else {
            emit({ready: true, enabled: false});
        }

        try {
            const remoteRaw = await readRemote(WORKSPACES_NAMESPACE);
            const remote = normalizeState(remoteRaw);

            if (remote) {
                const picked = pickNewer(local, remote);

                if (picked !== snapshot.state) {
                    adopt(picked, true);
                }

                synced = true;
                await writeMirror({state: picked, synced: true});
            } else if (local && synced) {
                // The account has no entry any more although we had synced one: it was
                // switched off on another device. Follow it instead of resurrecting it.
                adopt(null, false);
                await writeMirror(null);
            } else if (local) {
                // Enabled offline and never synced: push it now.
                schedule();
            }

            emit({offline: false});
        } catch (_error) {
            emit({offline: true});
        }

        emit({loading: false});
    };

    return {
        /** @returns {object} Current immutable snapshot. */
        getSnapshot: () => snapshot,

        /**
         * @param {() => void} listener Change listener.
         * @returns {() => void} Unsubscribe.
         */
        subscribe: (listener) => {
            listeners.add(listener);

            return () => listeners.delete(listener);
        },

        /**
         * Load once (idempotent). Resolves when the account answered or failed.
         *
         * @returns {Promise<void>} Completion.
         */
        ensureLoaded: () => {
            if (!loadPromise) {
                loadPromise = load();
            }

            return loadPromise;
        },

        /** Force the next `ensureLoaded` to hit the account again (pull-to-refresh). */
        reload: () => {
            loadPromise = load();

            return loadPromise;
        },

        /**
         * Turn workspaces on: runs the migration of the old layout into the first workspace.
         *
         * @param {{name?: string}} [options] Localized name of the first workspace.
         * @returns {object} The new state.
         */
        enable: ({name} = {}) => {
            migrationName = name || migrationName;

            const {state} = resolveInitialState(snapshot.state, {name: migrationName, now: now()});

            synced = false;
            adopt(state, true);
            writeMirror({state, synced: false}).catch(() => {});
            schedule();

            return state;
        },

        /**
         * Turn workspaces off: delete ONLY the `mobileWorkspaces` namespace.
         * The classic dashboard (and its own layout) is exactly as it was.
         *
         * @returns {Promise<void>} Completion.
         */
        disable: async () => {
            if (timer) {
                clearTimer(timer);
                timer = null;
            }

            adopt(null, false);
            await writeMirror(null).catch(() => {});
            await saveRemote({[WORKSPACES_NAMESPACE]: null}).catch(() => emit({offline: true}));
        },

        /**
         * Apply a pure state transform optimistically and schedule the save.
         *
         * @param {(state: object) => object} transform Pure transform (return the same reference for a no-op).
         * @returns {{changed: boolean, error: ?string}} `tooLarge` when the entry would exceed the namespace cap.
         */
        mutate: (transform) => {
            const current = snapshot.state;

            if (!current || !snapshot.enabled) {
                return {changed: false, error: 'disabled'};
            }

            const next = transform(current);

            if (next === current) {
                return {changed: false, error: null};
            }

            if (!fitsBudget(next)) {
                return {changed: false, error: 'tooLarge'};
            }

            const stamped = {...next, updatedAt: now().toISOString()};

            synced = false;
            emit({state: stamped, saving: true});
            writeMirror({state: stamped, synced: false}).catch(() => {});
            schedule();

            return {changed: true, error: null};
        },

        /**
         * Switch tab. The active tab is a view choice, so it is mirrored locally at once
         * but only travels to the account with the next real edit (no save per swipe).
         *
         * @param {string} boardId Board id.
         * @returns {void}
         */
        activate: (boardId) => {
            const current = snapshot.state;

            if (!current) {
                return;
            }

            const next = setActiveBoard(current, boardId);

            if (next !== current) {
                emit({state: next});
                writeMirror({state: next, synced}).catch(() => {});
            }
        },

        /** Flush a pending debounced save immediately (screen blur / app background). */
        flush: () => {
            if (timer) {
                clearTimer(timer);

                return flushSave();
            }

            return Promise.resolve();
        },
    };
};
